import React, { useEffect, useRef } from "react";
import '../calendar.css';
import { Filter } from "./Filters.tsx";

interface MultiSelectInputProps {
    list: MultiSelectInputItem[],
    selectedList: MultiSelectInputItem[],
    t?,
    setSelectedList,
    placeHolder,
    template?: (a, b: (id: any, change: any) => void) => React.ReactElement,
    update?: any,
    masonry?: boolean
    id_multiselector?: string | number
    check?: (element) => boolean
    icon?: React.ReactElement | boolean
    title?: string
}

export interface MultiSelectInputItem {
    id: string
    name: string
    color?: string
}

const MultiSelectInput = ({
    list,
    selectedList,
    t = (str) => str,
    setSelectedList,
    placeHolder,
    template = (element, b) => <div className="text-left w-full pl-2">{element.name}</div>,
    update = true,
    masonry = false,
    id_multiselector = 0,
    check = (element) => true,
    icon = true,
    title
}: MultiSelectInputProps) => {
    const LIMIT = 15;

    const [selectedListMutable, setSelectedListMutable] = React.useState([...selectedList]);
    const [shownList, setShownList] = React.useState([...list]);
    const [inputStr, setInputStr] = React.useState('');
    const [openDropdown, setOpenDropdown] = React.useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setShownList([...list])
        refreshShowedList();
    }, [list])

    useEffect(() => {
        setSelectedListMutable([...selectedList]);
    }, [selectedList, update])

    useEffect(() => {
        refreshShowedList();
    }, [selectedListMutable])

    useEffect(() => {
        function handleClickOutside(event) {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setOpenDropdown(false);
            }
        }
        if (openDropdown) {
            document.addEventListener("mousedown", handleClickOutside);
        } else {
            document.removeEventListener("mousedown", handleClickOutside);
        }
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, [openDropdown]);

    placeHolder = placeHolder + ' ';

    const drawSelectedElementTile = (element) => (
        <div
            key={element.id + '_' + id_multiselector}
            className={
                "flex items-center gap-2 px-2 py-1 rounded bg-slate-100 border border-slate-200 shadow-sm transition hover:bg-red-100"
                + (masonry ? " max-w-fit min-w-fit" : " w-full")
            }
            style={{ backgroundColor: element.color ? element.color + '20' : undefined }}
        >
            {template(element, handleChange)}
            <button
                type="button"
                className="ml-4 p-1 rounded-full hover:bg-red-400/80 hover:text-white text-red-500 transition"
                onClick={e => {
                    e.stopPropagation();
                    handleChange(element.id, 'delete');
                }}
                title={t('take off') + ' ' + element.name}
            >
                <svg className="w-3 h-3" aria-hidden="true" fill="none" viewBox="0 0 14 14">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                </svg>
                <span className="sr-only">Delete</span>
            </button>
        </div>
    );

    const drawShownElementTile = (element) => (
        <li
            key={element.id + '_' + id_multiselector}
            className="w-full"
        >
            <label
                htmlFor={element.id + '_' + id_multiselector}
                className="flex items-center gap-2 px-2 py-1 rounded cursor-pointer border border-transparent hover:border-green-400 hover:bg-green-50 transition"
                style={{ backgroundColor: element.color ? element.color + '10' : undefined }}
                title={t('add') + ' ' + element.name}
            >
                <input
                    id={element.id + '_' + id_multiselector}
                    type="checkbox"
                    className="hidden"
                    onChange={() => handleChange(element.id, 'add')}
                />
                <span className="text-gray-700">{element.name}</span>
            </label>
        </li>
    );

    const refreshShowedList = () => {
        let newShownList: MultiSelectInputItem[] = [];
        for (let element of list) {
            if (!selectedListMutable.some(elmt => elmt.id === element.id)) {
                newShownList.push(element);
            }
        }
        setShownList(newShownList);
    }

    const handleChange = (id, change) => {
        switch (change) {
            case 'delete': {
                let index = selectedListMutable.map(element => element.id).indexOf(id);
                selectedListMutable.splice(index, 1);
            }; break;
            case 'add': {
                const element = list.find(element => element.id === id)
                if (check(element) && element != null) {
                    selectedListMutable.push(element);
                    setInputStr('')
                }
                else return;
                break;
            }
            default: return;
        }
        setSelectedList([...selectedListMutable], id_multiselector);
        refreshShowedList();
    }

    const sort = (list: MultiSelectInputItem[]) => {
        return list.filter((e, i) =>
            i < LIMIT && Filter.stringFilter(inputStr, e.name)
        );
    }

    return (update ? (
        <div className="w-full flex justify-center" ref={dropdownRef} style={{ position: "relative" }}>
            <div className="relative w-full">
            {(icon != null && icon) && (
                <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                {icon !== true ? icon : (
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 20 20">
                    <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m19 19-4-4m0-7A7 7 0 1 1 1 8a7 7 0 0 1 14 0Z" />
                    </svg>
                )}
                </div>
            )}
            <label
                htmlFor={'input_search_bar_multiselect_' + id_multiselector}
                className={
                "flex flex-wrap gap-1 items-center bg-white border border-gray-300 rounded-lg w-full p-2 focus-within:ring-2 focus-within:ring-blue-300 transition"
                + (icon ? ' pl-10' : '')
                }
                style={{ cursor: "pointer" }}
            >
                {title && <p className="font-medium text-sm text-center w-full mb-1">{title}</p>}
                {selectedListMutable.length > 0 &&
                <div className="flex flex-wrap gap-1 w-full">
                    {selectedListMutable.map(drawSelectedElementTile)}
                </div>
                }
                <input
                id={'input_search_bar_multiselect_' + id_multiselector}
                className="flex-1 min-w-[3rem] bg-transparent outline-none text-gray-800 placeholder-gray-400"
                style={{ width: inputStr.length > 3 ? ((inputStr.length * 2) + 'ch') : '3rem' }}
                placeholder={t(placeHolder)}
                type="text"
                onChange={e => setInputStr(e.target.value)}
                value={inputStr}
                onClick={e => {
                    e.stopPropagation();
                    setOpenDropdown((prev) => !prev);
                }}
                />
            </label>
            {openDropdown && (
                <div className="absolute left-0 right-0 mt-1 z-20" style={{ position: "absolute" }}>
                <ul className="max-h-60 w-full py-2 bg-white border border-gray-200 rounded-lg shadow-lg overflow-y-auto text-sm">
                    {shownList.length > 0 ? (
                    <>
                        {sort(shownList).map(drawShownElementTile)}
                        {shownList.length > LIMIT &&
                        <li className="rounded-lg text-slate-500 flex justify-center py-1">
                            {'... (' + (shownList.length - LIMIT) + ' ' + t('more') + ')'}
                        </li>
                        }
                    </>
                    ) : (
                    <li className="py-2 text-sm rounded-lg text-slate-400 flex justify-center">
                        {t("no element")}
                    </li>
                    )}
                </ul>
                </div>
            )}
            </div>
        </div>
    ) : null);
}

export default MultiSelectInput;
